import { TFunction } from 'i18next';
import { getDisplayValueForMaybeUndefinedValue } from '../../../../../../utils';
import { generateUniqueTicksFromMinMax } from '../../../../../utils';
import {
  TableData,
  TableViewHeader,
  TableViewRow,
} from '../../../../tableview/types';
import {
  AxisConfigType,
  SingleOrDoubleAxisConfig,
  XAxisConfig,
  YAxisConfig,
} from '../common/common-chart-types';
import {
  BENCHMARK_DATA_PREFIX,
  BOTTOM_AXIS_ID,
  TOP_AXIS_ID,
} from '../common/common-chart-utils/common-chart-configs';
import { getSortedLabels } from '../common/common-chart-utils/common-chart-utils';
import { lineChartStyles } from './line-chart-styles';
import {
  LineChartData,
  MuiLineChartDataConfig,
  ProcessLineChartDataOptions,
  ProcessLineChartTableDataOptions,
} from './line-chart-types';

export const getMuiLineChartDataConfigFromChartData = (
  chartData: LineChartData,
  options: ProcessLineChartDataOptions,
  colors: { baseColor: string; benchmarkColor: string }[]
): MuiLineChartDataConfig => {
  const { labels, dataMap, datasets } = chartData;
  const { selectedSortOrder, showLabels } = options;
  const sortedLabels = selectedSortOrder
    ? getSortedLabels(chartData, selectedSortOrder)
    : labels;
  let xAxisConfigs: SingleOrDoubleAxisConfig<XAxisConfig<string>>;
  const firstXAxisConfig: XAxisConfig<string> = {
    dataKey: BOTTOM_AXIS_ID,
    id: BOTTOM_AXIS_ID,
    scaleType: 'point',
    // TODO Shaq: Not sure if I want this here, but maybe I do. If I don't want this here,
    // Will have to change the dataMap to support undefined values.
    valueFormatter: (value) => getDisplayValueForMaybeUndefinedValue(value),
    tickLabelInterval: () => true,
  };
  if (chartData.hasBenchmarkData) {
    // setting topAxisColor, which we use for benchmark
    // to be the same as the first dataset's benchmark color
    // For multiple datasets, we'll have to decide what we want to do
    // if this is confusing
    const topAxisColor = colors[0].benchmarkColor;
    const secondXAxisConfig: XAxisConfig<string> = {
      dataKey: TOP_AXIS_ID,
      id: TOP_AXIS_ID,
      scaleType: 'point',
      tickLabelInterval: () => true,
      tickLabelStyle: {
        fill: topAxisColor,
      },
    };
    xAxisConfigs = {
      type: AxisConfigType.DOUBLE,
      firstAxisConfig: firstXAxisConfig,
      secondAxisConfig: secondXAxisConfig,
    };
  } else {
    xAxisConfigs = {
      type: AxisConfigType.SINGLE,
      firstAxisConfig: firstXAxisConfig,
    };
  }
  let dataset: MuiLineChartDataConfig['dataset'];
  let series: MuiLineChartDataConfig['series'];
  dataset = sortedLabels.map((label) => {
    const dataMapValues = dataMap[label];
    const datasetValues: Record<string, number> = Object.fromEntries(
      dataMapValues.map((data) => [data.datasetKey, data.value])
    );
    let datasetObj: Record<string, string> = {
      [BOTTOM_AXIS_ID]: label,
      ...datasetValues,
    };
    if (chartData.hasBenchmarkData) {
      const { data: benchmarkDataMapValues, benchmarkDateLabel } =
        chartData.labelsToBenchmarkDataMap[label];
      const benchmarkDatasetValues: Record<string, number> = Object.fromEntries(
        benchmarkDataMapValues.map((data) => [
          `${BENCHMARK_DATA_PREFIX}-${data.datasetKey}`,
          data.value,
        ])
      );
      datasetObj = {
        ...datasetObj,
        [TOP_AXIS_ID]: benchmarkDateLabel,
        ...benchmarkDatasetValues,
      };
    }
    return datasetObj;
  });
  const datasetsWithBenchmarkDatasets = datasets.flatMap((dataset, index) => {
    const { baseColor, benchmarkColor } = colors[index];
    if (chartData.hasBenchmarkData) {
      const benchmarkDataset = {
        value: `${BENCHMARK_DATA_PREFIX}-${dataset.value}`,
        label: dataset.label
          ? chartData.benchmarkDatasetLabelFormatter(dataset.label)
          : undefined,
        valueFormatter: dataset.valueFormatter,
        color: benchmarkColor,
      };
      return [{ ...dataset, color: baseColor }, benchmarkDataset];
    }
    return [{ ...dataset, color: baseColor }];
  });
  series = datasetsWithBenchmarkDatasets.map((dataset, index) => ({
    id: dataset.value,
    dataKey: dataset.value,
    label: dataset.label ?? dataset.value,
    valueFormatter: (val: number | null) => {
      const value = val ?? 0;
      return dataset.valueFormatter?.(value) ?? String(value);
    },
    connectNulls: true,
    showMark: showLabels,
    color: dataset.color,
  }));
  const seriesValues = series.flatMap((s) =>
    dataset?.map((d) => {
      return s.dataKey ? d[s.dataKey] : 0;
    })
  );

  const validSeriesValues = seriesValues.filter(
    (v): v is number => v !== null && v !== undefined
  );
  const hasNegativeValues = validSeriesValues.some((d) => d < 0);
  const min = hasNegativeValues ? Math.min(...validSeriesValues) : 0;
  const max =
    validSeriesValues.length > 0 ? Math.max(...validSeriesValues) : 100;

  const yAxisValueFormatter = (value: number) => {
    // using first dataset to determine y axis value configs
    // TODO: Improve the whole structure to support secondary axis
    const dataset = datasets[0];
    return dataset.valueFormatter?.(value) ?? String(value);
  };

  const yAxisConfigs: SingleOrDoubleAxisConfig<YAxisConfig<number>> = {
    type: AxisConfigType.SINGLE,
    firstAxisConfig: {
      id: 'left',
      position: 'left',
      min: hasNegativeValues ? undefined : 0, // If there is any negative number in the series, use the default min based on the data. Otherwise, use zero.
      valueFormatter: yAxisValueFormatter,
      tickInterval: generateUniqueTicksFromMinMax(
        min,
        max,
        yAxisValueFormatter
      ),
    },
  };

  const benchmarkSeriesDetails = chartData.hasBenchmarkData
    ? datasets.map((d, i) => ({
        id: `${BENCHMARK_DATA_PREFIX}-${d.value}`,
        benchmarkColor: colors[i].benchmarkColor,
      }))
    : [];
  const styles = lineChartStyles({
    benchmarkSeriesDetails,
  });
  return {
    dataset,
    xAxisConfigs,
    yAxisConfigs,
    series,
    styles,
  };
};

export const getMuiLineChartTableDataFromChartData = (
  chartData: LineChartData,
  options: ProcessLineChartTableDataOptions,
  t: TFunction
): TableData => {
  const { dataMap, datasets, labels } = chartData;
  const { selectedSortOrder } = options;
  const sortedLabels = selectedSortOrder
    ? getSortedLabels(chartData, selectedSortOrder)
    : labels;

  const headers: TableViewHeader[] = [
    {
      value: '',
    },
    ...datasets.map((dataset) => ({
      value: getDisplayValueForMaybeUndefinedValue(
        dataset.label ?? dataset.value
      ),
    })),
  ];

  if (chartData.hasBenchmarkData) {
    headers.push(
      {
        value: t('common:benchmark.benchmarkDate'),
      },
      ...datasets.map((dataset) => {
        const benchmarkDatasetLabel = chartData.benchmarkDatasetLabelFormatter(
          dataset.label ?? dataset.value
        );
        return {
          value: getDisplayValueForMaybeUndefinedValue(benchmarkDatasetLabel),
        };
      })
    );
  }
  const rows: TableViewRow[] = sortedLabels.map((label) => {
    const cells = datasets.map((dataset) => {
      const value =
        dataMap[label].find((data) => data.datasetKey === dataset.value)
          ?.value ?? 0;
      return {
        key: `${label}-${dataset.label}`,
        value: dataset.valueFormatter?.(value) ?? String(value),
      };
    });
    if (chartData.hasBenchmarkData) {
      const benchmarkDatasets = datasets.map((dataset) => {
        const benchmarkDataset = {
          value: `${BENCHMARK_DATA_PREFIX}-${dataset.value}`,
          label: dataset.label
            ? chartData.benchmarkDatasetLabelFormatter(dataset.label)
            : undefined,
          valueFormatter: dataset.valueFormatter,
        };
        return benchmarkDataset;
      });
      const { benchmarkDateLabel, data: benchmarkDataMap } =
        chartData.labelsToBenchmarkDataMap[label];
      cells.push(
        {
          key: benchmarkDateLabel,
          value: benchmarkDateLabel,
        },
        ...datasets.map((dataset) => {
          const benchmarkDataMap =
            chartData.labelsToBenchmarkDataMap[label].data;
          const value =
            benchmarkDataMap.find((data) => data.datasetKey === dataset.value)
              ?.value ?? 0;
          return {
            key: `${label}-${BENCHMARK_DATA_PREFIX}-${dataset.label}`,
            value: dataset.valueFormatter?.(value) ?? String(value),
          };
        })
      );
    }
    return {
      key: label,
      title: getDisplayValueForMaybeUndefinedValue(label),
      cells: cells,
    };
  });

  const formattedHeaders = headers.map((header) => ({
    value: getDisplayValueForMaybeUndefinedValue(header.value),
  }));

  return {
    headers: formattedHeaders,
    rows,
  };
};
